import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  inject,
  input,
  output,
  OnInit,
  signal,
  effect,
  model,
} from '@angular/core';
import {
  ControllableObjectMapItem,
  ControllableObjectMessageType,
  ControllableObjectType,
  ControllableObjectUpdateData,
  Item,
  MonitorItem,
  NavigationGeodataObject,
  ShowInMapOrDetailModeEnum,
  ShowOnFloorplanEnum,
  SignalRService,
} from '@cue/api';
import { CmUnityLocalizedStringPipe, FoldableDirective } from '@cue/utilities';
import { ButtonComponent } from '@cue/ui';
import {
  cueArrowUp,
  cueCheckCircle,
  cueExclamation,
  cueIcon,
  cueInfo,
  cueMinusCircle,
  cueMute,
  cuePlusCircle,
  cueRaindrops,
  cueScreen,
  cueSun,
  cueWarning,
  IconComponent,
} from '@cue/ui/icons';
import { Memoize } from 'typescript-memoize';
import { RepeatButtonIntervalData } from '@cue/data';
import { explicitEffect } from 'ngxtension/explicit-effect';
import { NgStyle } from '@angular/common';
import { injectDestroy } from 'ngxtension/inject-destroy';
import { takeUntil } from 'rxjs';
import { TimePickerModule } from '@progress/kendo-angular-dateinputs';
import { NumericTextBoxModule, SliderComponent } from '@progress/kendo-angular-inputs';

@Component({
  selector: 'cue-controllable-button',
  templateUrl: './controllable-button.component.html',
  styleUrls: ['./controllable-button.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CmUnityLocalizedStringPipe,
    ButtonComponent,
    IconComponent,
    FoldableDirective,
    NgStyle,
    TimePickerModule,
    NumericTextBoxModule,
    SliderComponent,
  ],
  host: {
    '[class.pointer-events-none]': 'vypnoutSracku()',
    '[class.!absolute]': 'vypnoutSracku()',
    '[class.showOnFloorplan-none]': 'navigationGeodataObject().showOnFloorplan === showOnFloorplanEnum.None',
    '[class.showOnFloorplanWhenActiveDetail]':
      'navigationGeodataObject().showOnFloorplanDetail !== showOnFloorplanEnum.None',
    '[class.showOnFloorplan]': 'navigationGeodataObject().showOnFloorplan !== showOnFloorplanEnum.None',
    '[class]': 'controllableItemList?.[0]?.data?.type ? controllableItemList[0].data.type : ""',
  },
})
export class ControllableButtonComponent implements AfterViewInit, AfterViewChecked, OnDestroy, OnInit {
  private signalRService = inject(SignalRService);
  private elementRef = inject(ElementRef);
  private readonly sliderActionThrottleMs = 80;
  ICONS = {
    cueRaindrops,
    cueSun,
    cueExclamation,
    cueInfo,
    cueScreen,
    cueCheckCircle,
    cueMute,
    cueMinusCircle,
    cuePlusCircle,
    cueWarning,
    cueArrowUp,
  };

  private itemHeightList: number[] = [];
  private skipItemIdxList: number[] = [];
  private _startedIntervalIdForItem: RepeatButtonIntervalData[] = [];
  private pendingSliderActionTimeouts = new Map<string, ReturnType<typeof setTimeout>>();
  private lastSliderActionSentAt = new Map<string, number>();
  private lastItemToUpdate = signal<ControllableObjectMapItem | null>(null);
  private readonly legacyLongNumberInputType = 'lonf-number-input-1';
  protected readonly messageTypeEnum = ControllableObjectMessageType;
  protected readonly showInMapOrDetailModeEnum = ShowInMapOrDetailModeEnum;
  protected showOnFloorplanEnum = ShowOnFloorplanEnum;
  private resizeObservers: ResizeObserver[] = [];

  controllableItemList = model<ControllableObjectMapItem[]>([]);
  requestOpenPoiDetail = output<void>();
  navigationGeodataObject = input<NavigationGeodataObject>();
  customResizeBasedOnZoomEnabled = input<boolean>();
  sizeCoefficient = input<number>();
  zoomLevel = input<number>();
  applyTransformOnZoomLevelChange = explicitEffect([this.zoomLevel, this.sizeCoefficient], () => {
    this.applyTransform();
  });

  private showToggleButton: boolean[] = [];

  terminator = injectDestroy();
  ngOnInit() {
    this.signalRService
      .listen<ControllableObjectUpdateData>('ObjectChanged', false, '/MessageHub')
      .pipe(takeUntil(this.terminator))
      .subscribe((updatedData) => {
        if (!updatedData) {
          return;
        }

        if (this.controllableItemList()) {
          const updatedItemIdx = this.controllableItemList()!.findIndex((x) => x.objectId === updatedData.objectId);

          if (updatedItemIdx > -1) {
            this.lastItemToUpdate.set(this.controllableItemList()![updatedItemIdx]);
            let dataUpdated = this.updateItemData(updatedData);
            if (dataUpdated) {
              return;
            } else if (this.lastItemToUpdate().data?.group) {
              dataUpdated = this.updateGroupItemData(updatedData);
              if (dataUpdated) {
                return;
              } else {
                dataUpdated = this.updateGroupMessageData(updatedData);
              }
            }
            if (!dataUpdated) {
              dataUpdated = this.updateIndicatorData(updatedData);
              if (!dataUpdated) {
                this.updateMainMessage(updatedData);
              }
            }
          }
        }
      });
  }

  ngAfterViewInit(): void {
    this.observeTransformChanges();
  }

  vypnoutSracku = input(false);
  ngAfterViewChecked(): void {
    if (!this.vypnoutSracku()) {
      this.centerObject();
    }
  }

  private observeTransformChanges() {
    const observer = new MutationObserver(() => {
      this.centerObject();
    });

    observer.observe(this.elementRef.nativeElement, { attributes: true, attributeFilter: ['style'] });
  }

  private centerObject() {
    if (this.vypnoutSracku()) return;
    if (this.elementRef.nativeElement) {
      const element = this.elementRef.nativeElement;
      const elementWidth = element.offsetWidth;
      const computedStyle = getComputedStyle(element);
      const transform = computedStyle.transform;
      let scale = 1;

      if (transform && transform !== 'none') {
        const match = transform.match(/matrix.*\((.+)\)/);
        if (match) {
          const values = match[1].split(', ');
          scale = parseFloat(values[0]);
        }
      }

      const leftValue = (elementWidth / 2) * scale - 13 * scale; // 13 = half of poi width (26px / 2)
      element.style.left = `-${leftValue}px`;

      elementWidth > 25
        ? element.classList.add('custom-transform-origin')
        : element.classList.remove('custom-transform-origin');
    }
  }

  ngOnDestroy(): void {
    this.resizeObservers.forEach((observer) => observer.disconnect());
    this.pendingSliderActionTimeouts.forEach((timeoutId) => clearTimeout(timeoutId));
    this.pendingSliderActionTimeouts.clear();
    this.lastSliderActionSentAt.clear();
  }

  private updateMainMessage(updatedData: ControllableObjectUpdateData) {
    this.lastItemToUpdate.update((oldData) => ({
      ...oldData,
      data: {
        ...oldData.data,
        message: {
          ...oldData.data.message,
          ...updatedData.data,
        },
      },
    }));

    this.updateListData();
  }

  private updateIndicatorData(updatedData: ControllableObjectUpdateData): boolean {
    if (!this.isIndicatorType(this.lastItemToUpdate().data.type)) {
      return false;
    }

    if (this.lastItemToUpdate().objectId === updatedData.objectId) {
      this.lastItemToUpdate.update((old) => ({
        ...old,
        data: {
          ...old.data,
          ...updatedData.data,
        },
      }));
      return this.updateListData();
    }
    return false;
  }

  private updateItemData(updatedData: ControllableObjectUpdateData): boolean {
    const idxToUpdate = this.lastItemToUpdate().data.item?.findIndex((x) => x.id == updatedData.data?.id);
    if (idxToUpdate > -1) {
      this.lastItemToUpdate.update((old) => ({
        ...old,
        data: {
          ...old.data,
          item: old.data.item.map((item, index) => {
            if (index !== idxToUpdate) {
              return item;
            }
            return {
              ...item,
              ...updatedData.data,
            };
          }),
        },
      }));
      return this.updateListData();
    }
    return false;
  }

  private updateGroupItemData(updatedData: ControllableObjectUpdateData): boolean {
    const groups = this.lastItemToUpdate().data.group;
    if (!groups) {
      return false;
    }

    let targetGroupIdx = -1;
    let targetItemIdx = -1;

    groups.forEach((group, gIdx) => {
      const itemIdx = group.item.findIndex((item) => item.id === updatedData.data.id);
      if (itemIdx > -1) {
        targetGroupIdx = gIdx;
        targetItemIdx = itemIdx;
      }
    });

    if (targetGroupIdx === -1 || targetItemIdx === -1) {
      return false;
    }

    this.lastItemToUpdate.update((old) => ({
      ...old,
      data: {
        ...old.data,
        group: old.data.group.map((g, gIdx) => {
          if (gIdx !== targetGroupIdx) {
            return g;
          }
          return {
            ...g,
            item: g.item.map((item, iIdx) => {
              if (iIdx !== targetItemIdx) {
                return item;
              }
              return {
                ...item,
                ...updatedData.data,
              };
            }),
          };
        }),
      },
    }));
    return this.updateListData();
  }

  private updateListData() {
    const last = this.lastItemToUpdate();
    if (!last) {
      return false;
    }

    this.controllableItemList.update((oldList) =>
      oldList.map((item) =>
        item.objectId === last.objectId ? ({ ...item, ...last } satisfies ControllableObjectMapItem) : item
      )
    );
    return true;
  }

  private updateGroupMessageData(updatedData: ControllableObjectUpdateData) {
    const groupIdxToUpdate = this.lastItemToUpdate().data.group?.findIndex((x) => x.message.id === updatedData.data.id);

    if (groupIdxToUpdate > -1) {
      this.lastItemToUpdate.update((old) => ({
        ...old,
        data: {
          ...old.data,
          group: old.data.group.map((group, idx) => {
            if (idx !== groupIdxToUpdate) {
              return group;
            }
            return {
              ...group,
              message: {
                ...group.message,
                ...updatedData.data,
              },
            };
          }),
        },
      }));
      return this.updateListData();
    }
    return false;
  }

  protected getDefaultIconAccordingStatus(statusType: ControllableObjectMessageType): cueIcon {
    switch (statusType) {
      case ControllableObjectMessageType.Success:
        return this.ICONS.cueCheckCircle;
      case ControllableObjectMessageType.Warning:
        return this.ICONS.cueWarning;
      case ControllableObjectMessageType.Error:
        return this.ICONS.cueExclamation;
      case ControllableObjectMessageType.Info:
        return this.ICONS.cueInfo;
      default:
        return null;
    }
  }

  protected performAction(item: Item, objectId: string, event: MouseEvent) {
    event.stopPropagation();
    this.signalRService.sendMessageSignalR(objectId, item.id, '/MessageHub');
  }

  protected repeatButtonStart(item: Item, objectId: string, event: MouseEvent) {
    this.performAction(item, objectId, event);
    const intervalId = window.setInterval(() => {
      this.performAction(item, objectId, event);
    }, item.repeatButtonInterval * 1000);
    this._startedIntervalIdForItem.push({
      itemId: item.id,
      startedIntervalId: intervalId,
    });
  }

  protected repeatButtonEnd(item: Item, event: MouseEvent) {
    event.stopPropagation();
    const itemIdxToDelete = this._startedIntervalIdForItem.findIndex((x) => x.itemId === item.id);
    if (itemIdxToDelete > -1) {
      clearInterval(this._startedIntervalIdForItem[itemIdxToDelete].startedIntervalId);
      this._startedIntervalIdForItem.splice(itemIdxToDelete, 1);
    }
  }

  private toggleVisibility(event: Event): void {
    const cueIconElement = (event.target as HTMLElement).closest('.toggle-button') as HTMLElement;
    const wrapperElement = cueIconElement?.closest('.wrapper') as HTMLElement;

    cueIconElement?.classList.toggle('flipped');
    wrapperElement?.classList.toggle('toggled', !wrapperElement.classList.contains('toggled'));
  }

  protected convertRgbaToRgb(alpha: number) {
    const color = this.navigationGeodataObject().qrCategoryColor;
    if (!color || color?.startsWith('rgb(')) {
      return color;
    }
    // Extract first 3 numbers from rgba
    const [r, g, b] = color.match(/\d+/g).map(Number);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  // @Memoize()
  protected getTextColorIfAvailable(item: ControllableObjectMapItem): string {
    if (!this.canShowHideableItemProperty(item, ShowInMapOrDetailModeEnum.Color)) {
      return '#212529';
    }
    return this.getTextColorBasedOnBg(item.data?.color);
  }

  protected getTextColorBasedOnBg(color?: string): string {
    if (!color) return '#212529';
    const [r, g, b] = color.match(/\d+/g).map(Number);
    const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    return luminance > 128 ? '#212529' : '#ffffff';
  }

  protected isOneItemWrapper(controllableItem: ControllableObjectMapItem): boolean {
    if (this.isValueInputType(controllableItem.data?.type)) {
      return (controllableItem.data?.item?.length ?? 0) <= 1 && !controllableItem.data?.label;
    }

    const group = controllableItem.data?.group;
    const hasSingleGroup = group?.length === 1;
    const singleGroupHasMaxTwoButtons = hasSingleGroup && group[0].item.length <= 2;
    const singleGroupNoLabelNoMessage = hasSingleGroup && !group[0].label && !group[0].message?.text;
    const noLabelAndMessage = !controllableItem.data.label && !controllableItem.data.message?.text && hasSingleGroup;

    return noLabelAndMessage && singleGroupNoLabelNoMessage && singleGroupHasMaxTwoButtons;
  }

  protected isOneColumnWrapper(controllableItem: ControllableObjectMapItem): boolean {
    if (this.isValueInputType(controllableItem.data?.type)) {
      return true;
    }

    const group = controllableItem.data?.group;
    const allGroupsHaveSingleItem = group?.every((groupItem) => groupItem.item.length === 1);

    return allGroupsHaveSingleItem;
  }

  protected onToggleClicked(event: Event, index: number): void {
    const updatedValue = !this.showToggleButton[index];
    this.showToggleButton[index] = updatedValue;

    if (updatedValue) {
      this.skipItemIdxList.push(index);
    } else {
      this.skipItemIdxList.splice(this.skipItemIdxList.indexOf(index), 1);
    }

    this.toggleVisibility(event);
  }

  // @Memoize((item: ControllableObjectMapItem, property: ShowInMapOrDetailModeEnum) => {
  //   if (item?.data) {
  //     return `${item.data.name}.${item.data.value}.${item.data.color}`;
  //   }
  //   return `${item?.objectId}.${item?.name}`;
  // })
  protected canShowHideableItemProperty(item: ControllableObjectMapItem, property: ShowInMapOrDetailModeEnum): boolean {
    return item.showInMap?.some((x) => x == property);
  }

  @Memoize((item: ControllableObjectMapItem) => {
    return `${item?.objectId}.${item?.name}`;
  })
  protected canShowItem(item: ControllableObjectMapItem): boolean {
    if (!item?.data) {
      return false;
    }

    switch (item.data.type) {
      case ControllableObjectType.Control:
        return this.canShowControl(item);
      case ControllableObjectType.Slider:
      case ControllableObjectType.TimeInput:
      case ControllableObjectType.LongNumberInput:
        return this.canShowValueInput(item);
      case ControllableObjectType.Monitor:
        return this.canShowMonitor(item);
      case ControllableObjectType.Indicator:
        return this.canShowIndicator(item);
      default:
        return false;
    }
  }

  private applyTransform() {
    if (this.elementRef && this.customResizeBasedOnZoomEnabled()) {
      let scaleValue;

      if (this.zoomLevel() >= 0.5) {
        scaleValue = this.sizeCoefficient() * this.zoomLevel();
      } else {
        scaleValue = this.sizeCoefficient() * 0.5;
      }

      if (!this.vypnoutSracku()) {
        this.elementRef.nativeElement.style.transform = `scale(${scaleValue})`;
      } else {
        this.elementRef.nativeElement.style.transform = `scale(${scaleValue}) translate3d(-50%, 0, 0)`;
      }
    }
  }

  private canShowControl(item: ControllableObjectMapItem): boolean {
    return (
      (this.canShowHideableItemProperty(item, ShowInMapOrDetailModeEnum.Label) && item.data.label != null) ||
      this.canShowHideableItemProperty(item, ShowInMapOrDetailModeEnum.Control)
    );
  }

  private canShowMonitor(item: ControllableObjectMapItem): boolean {
    return (
      this.canShowHideableItemProperty(item, ShowInMapOrDetailModeEnum.ObjectLabel) ||
      this.canShowHideableItemProperty(item, ShowInMapOrDetailModeEnum.Icon) ||
      this.canShowHideableItemProperty(item, ShowInMapOrDetailModeEnum.Label) ||
      this.canShowHideableItemProperty(item, ShowInMapOrDetailModeEnum.Value) ||
      this.canShowHideableItemProperty(item, ShowInMapOrDetailModeEnum.Unit)
    );
  }

  private canShowValueInput(item: ControllableObjectMapItem): boolean {
    return (
      this.canShowHideableItemProperty(item, ShowInMapOrDetailModeEnum.ObjectLabel) ||
      this.canShowHideableItemProperty(item, ShowInMapOrDetailModeEnum.Label) ||
      this.canShowHideableItemProperty(item, ShowInMapOrDetailModeEnum.Value) ||
      this.canShowHideableItemProperty(item, ShowInMapOrDetailModeEnum.Unit)
    );
  }

  private canShowIndicator(item: ControllableObjectMapItem): boolean {
    return (
      this.canShowHideableItemProperty(item, ShowInMapOrDetailModeEnum.Label) ||
      this.canShowHideableItemProperty(item, ShowInMapOrDetailModeEnum.Color) ||
      this.canShowHideableItemProperty(item, ShowInMapOrDetailModeEnum.Value)
    );
  }

  protected isControlType(type?: ControllableObjectType | string): boolean {
    return type === ControllableObjectType.Control;
  }

  protected isValueInputType(type?: ControllableObjectType | string): boolean {
    return this.isSliderType(type) || this.isTimeInputType(type) || this.isLongNumberInputType(type);
  }

  protected isSliderType(type?: ControllableObjectType | string): boolean {
    return type === ControllableObjectType.Slider;
  }

  protected isTimeInputType(type?: ControllableObjectType | string): boolean {
    return type === ControllableObjectType.TimeInput;
  }

  protected isLongNumberInputType(type?: ControllableObjectType | string): boolean {
    return type === ControllableObjectType.LongNumberInput || type === this.legacyLongNumberInputType;
  }

  protected isIndicatorType(type?: ControllableObjectType | string): boolean {
    return type === ControllableObjectType.Indicator;
  }

  protected isMonitorType(type?: ControllableObjectType | string): boolean {
    return type === ControllableObjectType.Monitor;
  }

  protected canShowValueInputObjectLabel(item: ControllableObjectMapItem): boolean {
    return (
      !!item?.data?.label &&
      (this.canShowHideableItemProperty(item, ShowInMapOrDetailModeEnum.ObjectLabel) ||
        this.canShowHideableItemProperty(item, ShowInMapOrDetailModeEnum.Label))
    );
  }

  protected hasValueInputContent(item?: MonitorItem): boolean {
    return !!item && (!!item.label || this.hasInputValue(item) || !!item.unit || this.hasInputLimits(item));
  }

  protected hasInputValue(item?: MonitorItem): boolean {
    return item?.value !== null && item?.value !== undefined && item?.value !== '';
  }

  protected hasInputLimits(item?: MonitorItem): boolean {
    return item?.minimum !== undefined || item?.maximum !== undefined;
  }

  protected getInputLimitsText(item?: MonitorItem): string {
    if (!item) {
      return '';
    }

    if (item.minimum !== undefined && item.maximum !== undefined) {
      return `${item.minimum} - ${item.maximum}`;
    }

    if (item.minimum !== undefined) {
      return `${item.minimum}`;
    }

    if (item.maximum !== undefined) {
      return `${item.maximum}`;
    }

    return '';
  }

  protected getInputValue(item?: MonitorItem): string | number {
    return (item?.value as string | number) ?? '';
  }

  protected getSliderValue(item?: MonitorItem): number {
    const value = Number(item?.value ?? item?.minimum ?? 0);
    return Number.isFinite(value) ? value : 0;
  }

  protected onSliderValueChanged(objectId: string, itemId: string, value: number | null): void {
    this.onValueInputChanged(objectId, itemId, `${value ?? 0}`, ControllableObjectType.Slider);
    this.scheduleSliderValueInputAction(objectId, itemId);
  }

  protected onLongNumberValueChanged(objectId: string, itemId: string, value: number | null): void {
    this.onValueInputChanged(objectId, itemId, value == null ? '' : `${value}`, ControllableObjectType.LongNumberInput);
    this.performValueInputAction(objectId, itemId);
  }

  protected stopEditableInputEvent(event: Event): void {
    event.stopPropagation();
  }

  protected onValueInputChanged(
    objectId: string,
    itemId: string,
    rawValue: string,
    type?: ControllableObjectType | string
  ): void {
    const value = this.normalizeValueInputValue(rawValue, type);

    this.controllableItemList.update((items) =>
      items.map((controllableItem) =>
        controllableItem.objectId === objectId
          ? {
              ...controllableItem,
              data: {
                ...controllableItem.data,
                item: (controllableItem.data.item ?? []).map((item) =>
                  item.id === itemId
                    ? {
                        ...item,
                        value,
                      }
                    : item
                ),
              },
            }
          : controllableItem
      )
    );
  }

  protected getTimePickerValue(item?: MonitorItem): Date | null {
    const value = item?.value;
    if (typeof value !== 'string') {
      return null;
    }

    const [hours, minutes] = value.split(':').map(Number);
    if (Number.isNaN(hours) || Number.isNaN(minutes)) {
      return null;
    }

    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    return date;
  }

  protected onTimePickerValueChanged(objectId: string, itemId: string, value: Date | null): void {
    this.onValueInputChanged(objectId, itemId, this.formatTimePickerValue(value), ControllableObjectType.TimeInput);
    this.performValueInputAction(objectId, itemId);
  }

  private normalizeValueInputValue(rawValue: string, type?: ControllableObjectType | string): string | number {
    if (this.isSliderType(type)) {
      const parsed = Number(rawValue);
      return Number.isFinite(parsed) ? parsed : 0;
    }

    if (this.isLongNumberInputType(type)) {
      if (rawValue === '') {
        return '';
      }

      const parsed = Number(rawValue);
      return Number.isFinite(parsed) ? Math.trunc(parsed) : rawValue;
    }

    return rawValue;
  }

  private formatTimePickerValue(value: Date | null): string {
    if (!value) {
      return '';
    }

    const hours = `${value.getHours()}`.padStart(2, '0');
    const minutes = `${value.getMinutes()}`.padStart(2, '0');
    return `${hours}:${minutes}`;
  }

  private performValueInputAction(objectId: string, itemId: string): void {
    this.signalRService.sendMessageSignalR(objectId, itemId, '/MessageHub');
  }

  private scheduleSliderValueInputAction(objectId: string, itemId: string): void {
    const actionKey = `${objectId}:${itemId}`;

    if (this.pendingSliderActionTimeouts.has(actionKey)) {
      return;
    }

    const now = Date.now();
    const lastSentAt = this.lastSliderActionSentAt.get(actionKey) ?? 0;
    const elapsed = now - lastSentAt;

    if (elapsed >= this.sliderActionThrottleMs) {
      this.lastSliderActionSentAt.set(actionKey, now);
      this.performValueInputAction(objectId, itemId);
      return;
    }

    const timeoutId = setTimeout(() => {
      this.pendingSliderActionTimeouts.delete(actionKey);
      this.lastSliderActionSentAt.set(actionKey, Date.now());
      this.performValueInputAction(objectId, itemId);
    }, this.sliderActionThrottleMs - elapsed);

    this.pendingSliderActionTimeouts.set(actionKey, timeoutId);
  }

  protected updateItemHeight(itemIdx: number, itemHeight: number) {
    const foldableItemIdx = this.skipItemIdxList.indexOf(itemIdx);
    if (foldableItemIdx == -1) {
      this.itemHeightList[itemIdx] = itemHeight;
    }
  }

  protected canBeFolded(itemIdx: number): boolean {
    return this.itemHeightList[itemIdx] >= 50;
  }
}
